import { useCallback, useEffect, useState } from 'react';
import { settingsStore, type SettingRecord } from './SettingsStore';
import type { PendingFile } from '../../components/forms/ImageUpload';
import { useI18n } from '../../i18n/I18nContext';

const EMPTY: SettingRecord = {
  projectSettingId: '',
  coverImage: '',
  logoImage: '',
  coverImageUrl: '',
  logoImageUrl: '',
  motto: '',
  welcomeMessage: '',
  address: '',
  addressLink: '',
  contactNo: '',
  contactEmail: '',
  themeColor: '#dc2626',
  buttonColor: '#dc2626',
  serviceColor: '#dc2626',
  offsiteName: '',
  externalName: '',
  remarkLabel: '',
  defaultLanguage: '',
  allowOffsiteBooking: true,
  allowExternalBooking: false,
  allowCustomerLogin: false,
  productSectionEnabled: true,
  commissionPercentage: '',
  offsiteInterval: '',
  onsiteInterval: '',
  offsiteHours: {},
  onsiteHours: {},
  invoiceNotes: '',
};

export function useSettings() {
  const { t } = useI18n();
  const [form, setForm] = useState<SettingRecord>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [pendingCover, setPendingCover] = useState<PendingFile | null>(null);
  const [pendingLogo, setPendingLogo] = useState<PendingFile | null>(null);

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => {
    settingsStore
      .fetch()
      .then(setForm)
      .catch(() => showToast('error', t('settings.loadFailed')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  const set = <K extends keyof SettingRecord>(key: K, value: SettingRecord[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await settingsStore.save(form, pendingCover, pendingLogo);
      setForm(updated);
      setPendingCover(null);
      setPendingLogo(null);
      showToast('success', t('settings.saveSuccess'));
    } catch {
      showToast('error', t('settings.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setPendingCover(null);
    setPendingLogo(null);
    settingsStore.fetch().then(setForm).catch(() => showToast('error', t('settings.reloadFailed')));
  };

  return {
    form, set, loading, saving, toast,
    pendingCover, setPendingCover,
    pendingLogo, setPendingLogo,
    handleSave, handleDiscard, showToast,
  };
}
